import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Pagination } from ".";

type PaginationSimpleProps = React.ComponentProps<"nav"> & {
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  disabled?: boolean;
  hideLabel?: boolean;
};

function PaginationSimple({
  className,
  page,
  totalPages,
  onPageChange,
  disabled = false,
  hideLabel = false,
  ...props
}: PaginationSimpleProps) {
  const isControlled = page !== undefined && totalPages !== undefined && totalPages > 0 && onPageChange;
  const currentPage = isControlled ? Math.min(Math.max(1, page), totalPages) : 1;
  const [inputValue, setInputValue] = React.useState(String(currentPage));
  const [open, setOpen] = React.useState(false);
  const pageFieldRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const pages = React.useMemo(
    () => (open && totalPages ? Array.from({ length: totalPages }, (_, index) => index + 1) : []),
    [open, totalPages]
  );

  React.useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  React.useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>("[data-current='true']")?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw === "") {
      setInputValue("");
      return;
    }
    setInputValue(raw);
  };

  const commitPage = React.useCallback(() => {
    const parsed = parseInt(inputValue, 10);
    if (!isControlled || Number.isNaN(parsed)) {
      setInputValue(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(1, parsed), totalPages!);
    setInputValue(String(clamped));
    if (clamped !== currentPage) {
      onPageChange!(clamped);
    }
  }, [inputValue, currentPage, totalPages, isControlled, onPageChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitPage();
      setOpen(false);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setInputValue(String(currentPage));
      setOpen(false);
    }
  };

  const selectPage = (pageNumber: number) => {
    setInputValue(String(pageNumber));
    setOpen(false);
    if (pageNumber !== currentPage) onPageChange!(pageNumber);
  };

  if (!isControlled) {
    return (
      <nav
        role="navigation"
        aria-label="pagination"
        data-slot="pagination-simple"
        className={cn("mx-auto flex w-full justify-center", className)}
        {...props}
      />
    );
  }

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination-simple"
      className={cn("mx-auto flex justify-center items-center gap-2", className)}
      {...props}
    >
      <Pagination.Content>
        <Pagination.Item>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Go to previous page"
            disabled={disabled || currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            className={cn((disabled || currentPage <= 1) && "pointer-events-none opacity-50")}
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
        </Pagination.Item>
        <div className="flex items-center gap-1.5">
          {!hideLabel && <span className="text-sm text-muted-foreground whitespace-nowrap">Page</span>}
          <div ref={pageFieldRef} className="relative">
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValue}
              onChange={handlePageInputChange}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onBlur={event => {
                if (pageFieldRef.current?.contains(event.relatedTarget as Node)) return;
                commitPage();
                setOpen(false);
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              aria-label="Current page"
              aria-expanded={open}
              className="h-8 w-14 pr-5 text-center text-sm"
            />
            <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-1.5 size-3 -translate-y-1/2 text-muted-foreground" />
            {open && (
              <div
                ref={listRef}
                className="absolute bottom-full left-1/2 z-50 mb-1 max-h-60 w-14 -translate-x-1/2 overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
              >
                {pages.map(pageNumber => (
                  <button
                    key={pageNumber}
                    type="button"
                    data-current={pageNumber === currentPage}
                    className={cn(
                      "flex w-full items-center justify-center rounded-sm px-1 py-1 text-sm hover:bg-accent",
                      pageNumber === currentPage && "bg-accent font-medium"
                    )}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => selectPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">of {totalPages}</span>
        </div>
        <Pagination.Item>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Go to next page"
            disabled={disabled || currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className={cn((disabled || currentPage >= totalPages) && "pointer-events-none opacity-50")}
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </Pagination.Item>
      </Pagination.Content>
    </nav>
  );
}

PaginationSimple.displayName = "PaginationSimple";

export { PaginationSimple };
